import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText } from '@garnish/core';
import { Form } from '@garnish/forms';
import { Modal } from '@garnish/overlays';

import {
  GroupOrderModalCloseButton,
  GroupOrderModalContent,
  GroupOrderModalContentBlock,
  GroupOrderModalFooter,
  GroupOrderModalScrollView,
} from '@order/features-group-ordering';
import { type ConvertToGroupOrderParams } from '@order/features-ordering';

import { useGroupOrderSetupForm } from '../../../GroupOrderSetupScreen/hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderConvertModal = (props: Props) => {
  const { visible, isConvertingGroupOrder, onClose, onSubmit } = props;

  const { formatMessage } = useIntl();
  const { form, maxLengths, rules } = useGroupOrderSetupForm();
  const { control, formState } = form;
  const { errors } = formState;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isConvertingGroupOrder) return;

    onClose();
  }, [isConvertingGroupOrder, onClose]);

  const submitForm: () => Promise<void> = form.handleSubmit(onSubmit);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      size="medium"
      fitHeight
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onRequestClose={handleClose}
    >
      <GroupOrderModalContent>
        <GroupOrderModalScrollView
          keyboardAware
          bottomOffset={theme.spacing['4']}
          showsVerticalScrollIndicator={false}
          bounces={false}
          keyboardShouldPersistTaps="handled"
        >
          <GroupOrderModalContentBlock style={styles.container}>
            <GroupOrderModalCloseButton
              accessibilityLabel={formatMessage(messages.closeA11yLabel)}
              palette="muted"
              onPress={handleClose}
            />

            <View style={styles.headerContainer}>
              <DisplayText sizeMatch={['32']}>
                <FormattedMessage {...messages.title} />
              </DisplayText>

              <BodyText sizeMatch={['16']} style={styles.subtitle}>
                <FormattedMessage {...messages.subtitle} />
              </BodyText>
            </View>

            <View style={styles.detailsContainer}>
              <BodyText sizeMatch={['16']} bold>
                <FormattedMessage {...messages.details} />
              </BodyText>

              <View style={styles.fieldsContainer}>
                <Form.TextField
                  name="hostName"
                  control={control}
                  rules={rules.name}
                  accessibilityLabel={formatMessage(messages.hostNameLabel)}
                  label={formatMessage(messages.hostNameLabel)}
                  notice={errors.hostName?.message}
                  maxLength={maxLengths.name}
                  hideSubmitButton
                  disabled={isConvertingGroupOrder}
                  autoCapitalize="words"
                  clearButtonType="icon"
                />
              </View>
            </View>
          </GroupOrderModalContentBlock>
        </GroupOrderModalScrollView>

        <GroupOrderModalFooter>
          <Button size="large-wide" isLoading={isConvertingGroupOrder} onPress={submitForm}>
            <FormattedMessage {...messages.submitCta} />
          </Button>
        </GroupOrderModalFooter>
      </GroupOrderModalContent>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Make it a group order',
    description: 'Bag | Convert to group order modal | Accessibility label',
  },
  closeA11yLabel: {
    defaultMessage: 'Close group order conversion',
    description: 'Bag | Convert to group order modal | Close button accessibility label',
  },
  title: {
    defaultMessage: 'Make it a group order',
    description: 'Bag | Convert to group order modal | Title',
  },
  subtitle: {
    defaultMessage:
      'Your bag becomes the group order. Others can add their items before you check out.',
    description: 'Bag | Convert to group order modal | Subtitle',
  },
  details: {
    defaultMessage: 'Details',
    description: 'Bag | Convert to group order modal | Details section title',
  },
  hostNameLabel: {
    defaultMessage: 'Name',
    description: 'Bag | Convert to group order modal | Host name field label',
  },
  submitCta: {
    defaultMessage: 'Send invites',
    description: 'Bag | Convert to group order modal | Submit CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['8'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  headerContainer: {
    gap: theme.spacing['2'],
  },
  subtitle: {
    color: theme.colors.CHARCOAL,
  },
  detailsContainer: {
    gap: theme.spacing['4'],
  },
  fieldsContainer: {
    gap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  isConvertingGroupOrder: boolean;
  onClose: () => void;
  onSubmit: (params: ConvertToGroupOrderParams) => void;
};
